import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { FileText, Download, Send, RefreshCw, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { useAdminAuth } from "../../context/AdminAuthContext";

const AdminInvoices = () => {
  const { authHeaders } = useOutletContext();
  const { token } = useAdminAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const loadInvoices = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/invoices`, { headers: authHeaders })
      .then((r) => setInvoices(r.data || []))
      .catch(() => toast.error("Facturen laden mislukt"))
      .finally(() => setLoading(false));
  };

  const loadSettings = () => {
    axios
      .get(`${API}/admin/invoice-settings`, { headers: authHeaders })
      .then((r) => setSettings(r.data))
      .catch(() => toast.error("Instellingen laden mislukt"));
  };

  useEffect(() => {
    loadInvoices();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/admin/invoice-settings`, settings, {
        headers: authHeaders,
      });
      setSettings(res.data);
      toast.success("Factuurinstellingen opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSavingSettings(false);
    }
  };

  const downloadPdf = async (orderId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${orderId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      // Open in new tab AND offer download
      window.open(url, "_blank");
      // Also trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = `factuur-${invoiceNumber || orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("PDF ophalen mislukt");
    }
  };

  const resend = async (orderId) => {
    if (!window.confirm("Factuur opnieuw versturen naar de klant?")) return;
    setActingId(orderId);
    try {
      const res = await axios.post(
        `${API}/admin/invoices/${orderId}/resend`,
        {},
        { headers: authHeaders }
      );
      toast.success(`Mail verstuurd naar ${res.data.sent_to}`);
      loadInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    } finally {
      setActingId(null);
    }
  };

  const regenerate = async (orderId) => {
    if (!window.confirm("PDF opnieuw genereren met de huidige instellingen?")) return;
    setActingId(orderId);
    try {
      await axios.post(
        `${API}/admin/invoices/${orderId}/regenerate`,
        {},
        { headers: authHeaders }
      );
      toast.success("Factuur opnieuw gegenereerd");
      loadInvoices();
    } catch {
      toast.error("Genereren mislukt");
    } finally {
      setActingId(null);
    }
  };

  const setField = (k) => (e) =>
    setSettings((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div data-testid="admin-invoices" className="space-y-8">
      {/* Header + settings toggle */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-mono-x tracking-widest text-[var(--tha-red)]">
            FACTUREN
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">
            BEHEER & <span className="neon-red-text">VERSTUUR.</span>
          </h1>
        </div>
        <button
          data-testid="toggle-invoice-settings"
          onClick={() => setShowSettings((v) => !v)}
          className="btn-ghost-red px-4 py-2 inline-flex items-center gap-2 text-xs"
        >
          <Settings size={14} /> {showSettings ? "VERBERG INSTELLINGEN" : "INSTELLINGEN"}
        </button>
      </div>

      {/* Settings panel */}
      {showSettings && settings && (
        <div
          data-testid="invoice-settings-panel"
          className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6"
        >
          <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)] mb-5">
            DEZE GEGEVENS VERSCHIJNEN OP ELKE FACTUUR
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bedrijfsnaam" value={settings.company_name || ""} onChange={setField("company_name")} />
            <Field label="KvK" value={settings.kvk || ""} onChange={setField("kvk")} />
            <Field label="BTW-ID" placeholder="NL00000000B01" value={settings.btw_id || ""} onChange={setField("btw_id")} />
            <Field label="IBAN" placeholder="NL00BANK0123456789" value={settings.iban || ""} onChange={setField("iban")} />
            <Field label="Adresregel 1" value={settings.address_line_1 || ""} onChange={setField("address_line_1")} />
            <Field label="Adresregel 2" value={settings.address_line_2 || ""} onChange={setField("address_line_2")} />
            <Field label="E-mailadres" value={settings.email || ""} onChange={setField("email")} />
            <Field
              label="BTW-tarief (%)"
              type="number"
              value={settings.vat_rate ?? 21}
              onChange={(e) => setSettings((s) => ({ ...s, vat_rate: Number(e.target.value) }))}
            />
            <div className="md:col-span-2">
              <Field
                label="Onderwerp van bedankmail"
                value={settings.email_subject || ""}
                onChange={setField("email_subject")}
              />
            </div>
            <div className="md:col-span-2">
              <Textarea
                label='Intro-tekst (gebruik {first_name} voor voornaam)'
                rows={4}
                value={settings.email_intro || ""}
                onChange={setField("email_intro")}
              />
            </div>
            <div className="md:col-span-2">
              <Textarea
                label="Afsluitende tekst"
                rows={2}
                value={settings.email_outro || ""}
                onChange={setField("email_outro")}
              />
            </div>
            <div className="md:col-span-2">
              <Textarea
                label="Footer-tekst op PDF-factuur"
                rows={2}
                value={settings.footer_note || ""}
                onChange={setField("footer_note")}
              />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              data-testid="save-invoice-settings"
              onClick={saveSettings}
              disabled={savingSettings}
              className="btn-red px-5 py-3 text-sm font-display tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Save size={14} />
              {savingSettings ? "OPSLAAN..." : "INSTELLINGEN OPSLAAN"}
            </button>
            <button
              onClick={loadSettings}
              className="btn-ghost-red px-5 py-3 text-sm inline-flex items-center gap-2"
            >
              <RefreshCw size={14} /> HERLAAD
            </button>
          </div>
          <p className="mt-4 text-xs text-[var(--tha-muted)]">
            Tip: druk na het opslaan op <span className="text-white">"PDF opnieuw genereren"</span> bij een bestaande factuur om hem met de nieuwe instellingen te re-renderen. Het factuurnummer blijft hetzelfde.
          </p>
        </div>
      )}

      {/* Invoices list */}
      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
          <FileText size={14} className="text-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-widest text-white">
            ALLE FACTUREN
          </span>
          <span className="ml-auto text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
            {invoices.length} TOTAAL
          </span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[var(--tha-muted)]">Laden...</div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--tha-muted)]">
            Nog geen facturen. Zodra een klant betaalt, verschijnt de factuur hier
            automatisch met de bedankmail erbij.
          </div>
        ) : (
          <ul>
            {invoices.map((inv) => (
              <li
                key={inv.id}
                data-testid={`invoice-row-${inv.invoice_number}`}
                className="px-5 py-4 border-b border-[var(--tha-border)] last:border-0 flex flex-wrap items-center gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-x text-sm text-white tracking-widest">
                      {inv.invoice_number}
                    </span>
                    <span className="text-xs text-[var(--tha-muted)]">
                      #{inv.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm text-white mt-1 truncate">
                    {inv.full_name} <span className="text-[var(--tha-muted)]">· {inv.email}</span>
                  </div>
                  <div className="text-xs text-[var(--tha-muted)] mt-0.5">
                    {inv.invoice_generated_at
                      ? new Date(inv.invoice_generated_at).toLocaleString("nl-NL", {
                          dateStyle: "long",
                          timeStyle: "short",
                        })
                      : "—"}
                  </div>
                </div>
                <div className="text-sm font-mono-x tracking-widest text-[var(--tha-red)] shrink-0">
                  € {inv.total.toFixed(2)}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    data-testid={`invoice-download-${inv.invoice_number}`}
                    onClick={() => downloadPdf(inv.id, inv.invoice_number)}
                    className="btn-ghost-red px-3 py-2 text-xs inline-flex items-center gap-1"
                    title="Bekijk en download PDF"
                  >
                    <Download size={12} /> PDF
                  </button>
                  <button
                    data-testid={`invoice-resend-${inv.invoice_number}`}
                    onClick={() => resend(inv.id)}
                    disabled={actingId === inv.id}
                    className="btn-ghost-red px-3 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-60"
                    title="Mail opnieuw versturen naar klant"
                  >
                    <Send size={12} /> VERSTUUR
                  </button>
                  <button
                    data-testid={`invoice-regenerate-${inv.invoice_number}`}
                    onClick={() => regenerate(inv.id)}
                    disabled={actingId === inv.id}
                    className="btn-ghost-red px-3 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-60"
                    title="PDF opnieuw genereren met huidige instellingen"
                  >
                    <RefreshCw size={12} /> HER-GEN
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, type = "text" }) => (
  <label className="block">
    <span className="block text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-1.5">
      {label.toUpperCase()}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3 py-2 bg-[#070707] border border-[var(--tha-border)] text-sm text-white focus:outline-none focus:border-[var(--tha-red)]"
    />
  </label>
);

const Textarea = ({ label, value, onChange, rows = 3 }) => (
  <label className="block">
    <span className="block text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-1.5">
      {label.toUpperCase()}
    </span>
    <textarea
      rows={rows}
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2 bg-[#070707] border border-[var(--tha-border)] text-sm text-white focus:outline-none focus:border-[var(--tha-red)] resize-y"
    />
  </label>
);

export default AdminInvoices;
